import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Image as ImageIcon,
  Plus,
  Check,
  AlertCircle,
  Star,
  Trash2,
  RotateCcw,
  Edit3
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import '../../styles/CuratorEditCollection.css';

const CuratorEditCollection = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();

  const [collection, setCollection] = useState(null);
  const [event, setEvent] = useState(null);
  const [removedEvent, setRemovedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCreateEvent, setShowCreateEvent] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image: '',
    featured: false
  });

  // Event form data
  const [eventFormData, setEventFormData] = useState({
    name: '',
    description: '',
    event_type: 'show',
    featured: false,
    capacity: '',
    ticket_price: '',
    tickets_available: '',
    ticket_sales_enabled: false,
    ticket_description: ''
  });

  useEffect(() => {
    if (collectionId) {
      loadCollectionData();
    }
  }, [collectionId]);

  const loadCollectionData = async () => {
    try {
      setLoading(true);

      // Verify user access
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Load collection
      const { data: collectionData, error: collectionError } = await supabase
        .from('collections')
        .select('*')
        .eq('collection_id', collectionId)
        .single();

      if (collectionError) throw collectionError;
      setCollection(collectionData);

      // Load collection form
      setFormData({
        title: collectionData.title || '',
        description: collectionData.description || '',
        cover_image: collectionData.cover_image || '',
        featured: collectionData.featured || false
      });

      // Load active event
      const { data: activeEvent, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('status', 'active')
        .single();

      if (!eventError && activeEvent) {
        setEvent(activeEvent);
        setEventFormData({
          name: activeEvent.name || '',
          description: activeEvent.description || '',
          event_type: activeEvent.event_type || 'show',
          featured: activeEvent.featured || false,
          capacity: activeEvent.capacity || '',
          ticket_price: activeEvent.ticket_price || '',
          tickets_available: activeEvent.tickets_available || '',
          ticket_sales_enabled: activeEvent.ticket_sales_enabled || false,
          ticket_description: activeEvent.ticket_description || ''
        });
      }

      // Load removed event (if exists)
      const { data: removedEventData } = await supabase
        .from('events')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('status', 'removed')
        .single();

      if (removedEventData) {
        setRemovedEvent(removedEventData);
      }

    } catch (err) {
      console.error('Error loading collection:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    try {
      setUploadingImage(true);
      setMessage({ type: '', text: '' });

      const uploadResult = await uploadToCloudinary(file, 'image', 'collections');
      setFormData(prev => ({ ...prev, cover_image: uploadResult.url }));

      setMessage({ type: 'success', text: 'Image uploaded successfully!' });
    } catch (err) {
      console.error('Error uploading image:', err);
      setMessage({ type: 'error', text: 'Failed to upload image. Please try again.' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEventFormChange = (field, value) => {
    setEventFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Collection title is required' });
      return false;
    }
    if (!formData.description.trim()) {
      setMessage({ type: 'error', text: 'Collection description is required' });
      return false;
    }
    return true;
  };

  const validateEventForm = () => {
    if (!eventFormData.name.trim()) {
      setMessage({ type: 'error', text: 'Event name is required' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Update collection
      const { error: updateError } = await supabase
        .from('collections')
        .update({
          title: formData.title,
          description: formData.description,
          cover_image: formData.cover_image,
          featured: formData.featured
        })
        .eq('collection_id', collectionId);

      if (updateError) throw updateError;

      setMessage({ type: 'success', text: 'Collection updated successfully!' });

      setTimeout(() => {
        navigate(`/curator/collections/${collectionId}`);
      }, 2000);

    } catch (err) {
      console.error('Error updating collection:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();

    if (!validateEventForm()) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const { data: { user } } = await supabase.auth.getUser();
      const { data: galleryMapping } = await supabase
        .from('gallery_collection_mapping')
        .select('gallery_id')
        .eq('collection_id', collectionId)
        .single();

      const { data: galleryData } = await supabase
        .from('galleries')
        .select('name, address')
        .eq('gallery_id', galleryMapping.gallery_id)
        .single();

      // Create event
      const { data: newEvent, error: eventError } = await supabase
        .from('events')
        .insert({
          name: eventFormData.name,
          description: eventFormData.description,
          event_type: eventFormData.event_type,
          event_start: null,
          event_end: null,
          featured: eventFormData.featured,
          status: 'active',
          gallery_id: galleryMapping.gallery_id,
          collection_id: collectionId,
          cover_image: collection.cover_image,
          capacity: eventFormData.capacity ? parseInt(eventFormData.capacity) : null,
          ticket_price: eventFormData.ticket_price || null,
          tickets_available: eventFormData.tickets_available ? parseInt(eventFormData.tickets_available) : null,
          ticket_sales_enabled: eventFormData.ticket_sales_enabled,
          ticket_description: eventFormData.ticket_description || null,
          venue_name: galleryData.name,
          venue_address: galleryData.address,
          timezone: 'America/Chicago'
        })
        .select()
        .single();

      if (eventError) throw eventError;

      setEvent(newEvent);
      setShowCreateEvent(false);
      setMessage({ type: 'success', text: 'Event created successfully!' });

    } catch (err) {
      console.error('Error creating event:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveEvent = async () => {
    if (!window.confirm('Remove this event? You can restore it later from the removed section.')) {
      return;
    }

    try {
      setSaving(true);

      const { error } = await supabase
        .from('events')
        .update({ status: 'removed' })
        .eq('id', event.id);

      if (error) throw error;

      setRemovedEvent(event);
      setEvent(null);
      setMessage({ type: 'success', text: 'Event removed successfully' });

    } catch (err) {
      console.error('Error removing event:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreEvent = async () => {
    try {
      setSaving(true);

      const { error } = await supabase
        .from('events')
        .update({ status: 'active' })
        .eq('id', removedEvent.id);

      if (error) throw error;

      setEvent(removedEvent);
      setRemovedEvent(null);
      setMessage({ type: 'success', text: 'Event restored successfully' });

    } catch (err) {
      console.error('Error restoring event:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Permanently delete this event? This cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);

      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', removedEvent.id);

      if (error) throw error;

      setRemovedEvent(null);
      setMessage({ type: 'success', text: 'Event permanently deleted' });

    } catch (err) {
      console.error('Error deleting event:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="curator-edit-collection">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading collection...</p>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="curator-edit-collection">
        <div className="error-state">
          <h3>Collection Not Found</h3>
          <p>The collection you're trying to edit doesn't exist.</p>
          <button onClick={() => navigate('/curator')} className="btn-primary">
            Back to Overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-edit-collection">
      {/* Header */}
      <div className="edit-header">
        <button
          className="back-button"
          onClick={() => navigate(`/curator/collections/${collectionId}`)}
        >
          <ArrowLeft size={20} />
          Back to Collection
        </button>

        <div className="header-content">
          <h1>Edit Collection</h1>
          <p>{collection.title}</p>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          <div className="message-content">
            {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
            <span>{message.text}</span>
          </div>
          <button
            className="message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </button>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="edit-form">
        {/* Collection Details Section */}
        <div className="form-section">
          <h2>Collection Details</h2>

          {/* Cover Image */}
          <div className="image-upload-section">
            <h3>Cover Image</h3>
            <div className="image-upload-area">
              {formData.cover_image ? (
                <div className="uploaded-image">
                  <img src={formData.cover_image} alt="Collection cover" />
                  <div className="image-overlay">
                    <label className="upload-btn">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleImageUpload(file);
                        }}
                        style={{ display: 'none' }}
                      />
                      <span>
                        <ImageIcon size={20} />
                        {uploadingImage ? 'Uploading...' : 'Change Image'}
                      </span>
                    </label>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    id="cover-image-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageUpload(file);
                    }}
                    style={{ display: 'none' }}
                  />
                  <div
                    className="upload-placeholder"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = document.getElementById('cover-image-input');
                      if (input) {
                        input.click();
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {uploadingImage ? (
                      <div className="uploading">
                        <div className="loading-spinner"></div>
                        <p>Uploading...</p>
                      </div>
                    ) : (
                      <div>
                        <ImageIcon size={48} />
                        <p>Click to upload new cover image</p>
                        <span>Recommended: Square image, min 800x800px</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Title & Description */}
          <div className="form-field">
            <label htmlFor="title">Collection Title *</label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              placeholder="Enter collection title"
              required
            />
          </div>

          <div className="form-field">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Describe this collection..."
              rows={4}
              required
            />
          </div>

          <div className="form-field checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => handleFormChange('featured', e.target.checked)}
              />
              <Star size={16} />
              Featured Collection
            </label>
          </div>
        </div>

        {/* Event Management Section */}
        <div className="form-section event-section">
          <h2>Exhibition Event</h2>

          {event ? (
            <>
              <div className="event-summary">
                <div className="event-info">
                  <h3>{event.name}</h3>
                  <p className="event-type">{event.event_type.toUpperCase()}</p>
                  {event.description && (
                    <p className="event-description">{event.description}</p>
                  )}
                </div>
                <div className="event-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => navigate(`/curator/events/${event.id}/edit`)}
                  >
                    <Edit3 size={16} />
                    Edit Event
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleRemoveEvent}
                    disabled={saving}
                  >
                    <Trash2 size={16} />
                    Remove
                  </button>
                </div>
              </div>
            </>
          ) : removedEvent ? (
            <>
              <div className="removed-event-notice">
                <AlertCircle size={20} />
                <div>
                  <h4>Event Removed</h4>
                  <p>{removedEvent.name}</p>
                </div>
              </div>
              <div className="removed-event-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleRestoreEvent}
                  disabled={saving}
                >
                  <RotateCcw size={16} />
                  Restore Event
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDeleteEvent}
                  disabled={saving}
                >
                  <Trash2 size={16} />
                  Delete Permanently
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="no-event-message">
                No exhibition event associated with this collection.
              </p>
              {!showCreateEvent ? (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateEvent(true)}
                >
                  <Plus size={16} />
                  Create Event
                </button>
              ) : (
                <div className="create-event-form">
                  <h3>Create Exhibition Event</h3>

                  <div className="form-grid">
                    <div className="form-field">
                      <label htmlFor="event_name">Event Name *</label>
                      <input
                        id="event_name"
                        type="text"
                        value={eventFormData.name}
                        onChange={(e) => handleEventFormChange('name', e.target.value)}
                        placeholder="Event name"
                        required
                      />
                    </div>

                    <div className="form-field">
                      <label htmlFor="event_type">Event Type</label>
                      <select
                        id="event_type"
                        value={eventFormData.event_type}
                        onChange={(e) => handleEventFormChange('event_type', e.target.value)}
                      >
                        <option value="show">Art Show</option>
                        <option value="opening">Opening Reception</option>
                        <option value="exhibition">Exhibition</option>
                        <option value="workshop">Workshop</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="event_description">Description</label>
                    <textarea
                      id="event_description"
                      value={eventFormData.description}
                      onChange={(e) => handleEventFormChange('description', e.target.value)}
                      placeholder="Describe the event..."
                      rows={3}
                    />
                  </div>

                  <div className="form-field checkbox-field">
                    <label>
                      <input
                        type="checkbox"
                        checked={eventFormData.featured}
                        onChange={(e) => handleEventFormChange('featured', e.target.checked)}
                      />
                      <Star size={16} />
                      Featured Event
                    </label>
                  </div>

                  <div className="create-event-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowCreateEvent(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleCreateEvent}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <div className="loading-spinner small"></div>
                          Creating...
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          Create Event
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/curator/collections/${collectionId}`)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving || uploadingImage}
          >
            {saving ? (
              <>
                <div className="loading-spinner small"></div>
                Saving...
              </>
            ) : (
              <>
                <Check size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CuratorEditCollection;
